import { useState, useEffect, useMemo } from 'react';
import { getGroceryShopProducts, getPublicGroceryDishes, listGroceryShops } from '../services/groceryShopApi';

const ITEMS_PER_PAGE = 12;

const mapDishRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((d) => ({
    id: d.id,
    source: 'dish',
    name: d.name,
    image_url: d.dish_image_url,
    price: d.price,
    category: d.category_name,
    restaurant_name: d.restaurant_name,
    origin_country: null,
    origin_region: null,
    is_african: false,
    unit: 'piece',
  }));

/**
 * État + logique du catalogue grocery shop (magasins, produits, catégories, pagination).
 * Partagé entre l'accueil (onglet Grocery shop) et la page dédiée /store.
 */
export default function useGroceryFeed() {
  const [groceryShops, setGroceryShops] = useState([]);
  const [selectedGroceryShopId, setSelectedGroceryShopId] = useState(null);
  const [selectedGroceryCategory, setSelectedGroceryCategory] = useState(null);
  const [groceryShopProducts, setGroceryShopProducts] = useState([]);
  const [groceryShopLoading, setGroceryShopLoading] = useState(true);
  const [groceryPage, setGroceryPage] = useState(1);

  // Charger la liste des magasins une seule fois
  useEffect(() => {
    listGroceryShops(true)
      .then((shops) => setGroceryShops(Array.isArray(shops) ? shops : []))
      .catch(() => setGroceryShops([]));
  }, []);

  // Charger les produits — re-fetch quand le magasin sélectionné change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setGroceryShopLoading(true);
        if (selectedGroceryShopId !== null) {
          const selectedShop = groceryShops.find((s) => s.id === selectedGroceryShopId);
          const [catalog, dishRows] = await Promise.all([
            getGroceryShopProducts({ grocery_shop_id: selectedGroceryShopId, limit: 200, active_only: true }),
            selectedShop?.restaurant_id
              ? getPublicGroceryDishes({ restaurant_id: selectedShop.restaurant_id, limit: 200 }).catch(() => [])
              : Promise.resolve([]),
          ]);
          const catalogNorm = (Array.isArray(catalog) ? catalog : []).map((p) => ({ ...p, source: 'catalog' }));
          if (!cancelled) setGroceryShopProducts([...catalogNorm, ...mapDishRows(dishRows)]);
        } else {
          const [catalog, dishRows] = await Promise.all([
            getGroceryShopProducts({ limit: 200, active_only: true }),
            getPublicGroceryDishes({ limit: 200 }).catch(() => []),
          ]);
          const catalogNorm = (Array.isArray(catalog) ? catalog : []).map((p) => ({ ...p, source: 'catalog' }));
          if (!cancelled) setGroceryShopProducts([...catalogNorm, ...mapDishRows(dishRows)]);
        }
      } catch (e) {
        console.error('Grocery shop:', e);
        if (!cancelled) setGroceryShopProducts([]);
      } finally {
        if (!cancelled) setGroceryShopLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedGroceryShopId, groceryShops]);

  const groceryCategories = useMemo(() => {
    const categories = new Set();
    (groceryShopProducts || []).forEach((product) => {
      categories.add(product.category?.trim() || 'Autres');
    });
    return Array.from(categories);
  }, [groceryShopProducts]);

  const filteredGroceryProducts = useMemo(() => {
    const list = groceryShopProducts || [];
    if (!selectedGroceryCategory || selectedGroceryCategory === 'Tous') return list;
    return list.filter((product) => (product.category?.trim() || 'Autres') === selectedGroceryCategory);
  }, [groceryShopProducts, selectedGroceryCategory]);

  const groceryTotalPages = Math.max(1, Math.ceil((filteredGroceryProducts?.length || 0) / ITEMS_PER_PAGE));
  const groceryPageSafe = Math.min(groceryPage, groceryTotalPages);
  const pagedGroceryProducts = useMemo(() => {
    const list = filteredGroceryProducts || [];
    const start = (groceryPageSafe - 1) * ITEMS_PER_PAGE;
    return list.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredGroceryProducts, groceryPageSafe]);

  useEffect(() => {
    setGroceryPage((p) => Math.min(p, groceryTotalPages));
  }, [groceryTotalPages]);

  useEffect(() => {
    setSelectedGroceryCategory(null);
    setGroceryPage(1);
  }, [selectedGroceryShopId]);

  const handleGroceryPageChange = (pageNumber) => {
    setGroceryPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGroceryCategoryClick = (category) => {
    setSelectedGroceryCategory(category === 'Tous' ? null : category);
    setGroceryPage(1);
  };

  return {
    groceryShops,
    selectedGroceryShopId,
    setSelectedGroceryShopId,
    selectedGroceryCategory,
    groceryShopProducts,
    groceryShopLoading,
    groceryCategories,
    filteredGroceryProducts,
    pagedGroceryProducts,
    groceryPage,
    setGroceryPage,
    groceryPageSafe,
    groceryTotalPages,
    handleGroceryPageChange,
    handleGroceryCategoryClick,
  };
}
